import React, { useEffect, useState, useRef } from "react";
import { useDialog } from "../../../contexts/DialogContext";
import { getTimeStamp } from "../../../utils/common";
import useAuth from "../../../hooks/useAuth";
import { useFormContext } from "react-hook-form";

import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Button,
  Typography,
  Stack,
  Box,
  useMediaQuery,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import useKycDocument from "../../../hooks/useKycDocument";
import { useSearchParams } from "react-router-dom";
import UploadKycDocument from "../../users/user_details/UploadKycDocument";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Approve from "../../users/user_details/Approve";
import RejectKycDocument from "../../users/user_details/RejectKycDocument";
import CustomTextField from "../../../components/CustomTextField";
import ReactHookFormSelect from "../../../components/ReactHookFormSelect";
import CustomFileField from "../../../components/CustomFileField";
import KycPreview from "./KycPreview";
import useCafForm from "../../../hooks/useCafForm";

function KycDocuments(props) {
  const { cafForm, getKycDocumentPreview } = useCafForm();
  const fileInputRef = useRef(null);
  const { t } = useTranslation();
  const {
    formState: { errors },
    control,
    watch,
    setValue,
  } = useFormContext();

  const { validationFields } = props;

  let [searchParams] = useSearchParams();

  const { user } = useAuth();
  const { openDialog } = useDialog();
  const isMobile = useMediaQuery("(max-width:600px)");

  const kycDocumentId = searchParams.get("id");

  const currentPayload = {
    filters: [
      {
        fieldName: "radUserId",
        fieldValue: kycDocumentId,
        op: "eq",
        dataType: "int",
        condition: "and",
        isJoin: true,
        attributeName: "radUser",
        entityName: "RadUserEntity",
      },
    ],
    pagination: {
      pageNumber: 1,
      pageSize: 100,
    },
  };

  const { getAllKycDocument, kycDocuments, getKycDocumentImage } = useKycDocument();

  const [payload, setPayload] = useState(currentPayload);
  const [images, setImages] = useState({});

  useEffect(() => {
    if (kycDocumentId) {
      getAllKycDocument(payload);
    }
  }, [payload, kycDocumentId]);

  useEffect(() => {
    const loadImages = async () => {
      console.log(cafForm, "cafFormcafForm");
      const map = {};
      try {
        if (cafForm.addressProofPath) {
          const fileName = cafForm.addressProofPath.split("/").pop();
          const addressProofUrl = await getKycDocumentPreview(cafForm.cafId, fileName);
          map["addressProof"] = addressProofUrl;
        }

        if (cafForm.idProofPath) {
          const idProofFileName = cafForm.idProofPath.split("/").pop();
          const idProofUrl = await getKycDocumentPreview(cafForm.cafId, idProofFileName);
          map["idProof"] = idProofUrl;
        }
      } catch (error) {
        console.warn("Failed to load some KYC images", error);
      }

      setImages(map);
    };
    if (Object.keys(cafForm).length != 0) loadImages();
  }, [cafForm]);

  const hasNoDocuments = !kycDocuments || kycDocuments.length === 0;

  const uploadIdClick = (title) => {
    let dropDownValues = [];

    if (title === "Id Proof") {
      dropDownValues = [
        { key: "AADHAR_CARD", value: "Aadhar Card" },
        { key: "PAN_CARD", value: "Pan Card" },
        { key: "VOTER_ID", value: "Voter Id" },
        { key: "DRIVING_LIC", value: "Driving Licence" },
      ];
    }
    if (title === "Address Proof") {
      dropDownValues = [
        { key: "HOME_TAX", value: "Home Tax" },
        { key: "WATER_BILL", value: "Water Bill" },
        { key: "GAS_BILL", value: "Gas Bill" },
        { key: "GST", value: "GST" },
      ];
    }
    if (title === "Profile Proof") {
      dropDownValues = [{ key: "PROFILE_PIC", value: "Profile Pic" }];
    }
    if (title === "CAF Proof") {
      dropDownValues = [{ key: "CAF_PROOF", value: "Caf Proof" }];
    }

    return openDialog(
      "upload_id_proof",
      "Upload Id Proof",
      <UploadKycDocument
        key={getTimeStamp()}
        radUserId={searchParams.get("id")}
        dropDownValues={dropDownValues}
        documentTitle={title}
        refreshPage={() => getAllKycDocument(payload)}
      />,
    );
  };

  const approveClick = (id) => {
    return openDialog(
      "approve",
      "Approve",
      <Approve
        key={getTimeStamp()}
        kycDocumentId={id}
        refreshPage={() => getAllKycDocument(payload)}
      />,
    );
  };

  const rejectClick = (id) => {
    return openDialog(
      "reject",
      "Reject KYC",
      <RejectKycDocument
        key={getTimeStamp()}
        kycDocumentId={id}
        refreshPage={() => getAllKycDocument(payload)}
      />,
    );
  };

  const approveOrRejectPermission = () => {
    if (!user) return false;
    return ["SUPER_ADMIN", "MULTI_SERVICE_OPERATOR"].includes(user.businessType);
  };

  const downloadKycDocument = (url, title) => {
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", title);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const isPending = (status) => status && status.trim().toLowerCase() === "pending";

  // ---------------------------------------------------------
  // ✅ DEFAULT SCREEN WHEN NO DOCUMENTS FOUND
  // ---------------------------------------------------------
  if (hasNoDocuments) {
    return (
      <Grid container spacing={3}>
        {/* LEFT SIDE – Upload & Inputs */}
        <Grid item xs={12} md={12}>
          <Card sx={{ p: 3, borderRadius: 3, height: "100%" }}>
            <CardHeader title="Upload KYC Documents" />
            <CardContent>
              <Grid container spacing={6}>
                <Grid item xs={12} md={4}>
                  <ReactHookFormSelect
                    name="identityProofType"
                    label={t("id_proof_type")}
                    control={control}
                    fullWidth
                    variant="outlined"
                    errors={errors}
                    fields={validationFields}
                  >
                    <MenuItem value={"AADHAAR"}>Aadhar</MenuItem>
                    <MenuItem value={"VOTER_ID"}>Voter Id</MenuItem>
                    <MenuItem value={"PASSPORT"}>Passport</MenuItem>
                    <MenuItem value={"DRIVING_LICENSE"}>Driving License</MenuItem>
                    <MenuItem value={"PAN"}>Pan</MenuItem>
                    <MenuItem value={"GOVT_EMPLOYEE_ID"}>Government Employee Id</MenuItem>
                    <MenuItem value={"BANK_PASSBOOK"}>Bank Passbook</MenuItem>
                    <MenuItem value={"PSU_ID"}>Psu Id</MenuItem>
                    <MenuItem value={"DEFENCE_ID"}>Defense ID</MenuItem>
                    <MenuItem value={"PENSION_CARD"}>Pension Card</MenuItem>
                  </ReactHookFormSelect>
                </Grid>
                <Grid item xs={12} md={4}>
                  <CustomTextField
                    label="ID Proof Number"
                    name="identityProofNo"
                    control={control}
                    fullWidth
                    variant="outlined"
                    errors={errors}
                    fields={validationFields}
                  />
                </Grid>

                {/* ID PROOF */}
                <Grid item xs={12} md={4}>
                  <CustomFileField
                    label={t("id_proof")}
                    name="idProof"
                    control={control}
                    fullWidth
                    variant="outlined"
                    errors={errors}
                    fields={validationFields}
                    ref={fileInputRef}
                  />
                </Grid>

                {/* ADDRESS PROOF */}
                <Grid item xs={12} md={4}>
                  <ReactHookFormSelect
                    name="addressProofType"
                    label={t("address_proof_type")}
                    control={control}
                    fullWidth
                    variant="outlined"
                    errors={errors}
                    fields={validationFields}
                  >
                    <MenuItem value={"AADHAAR"}>Aadhar</MenuItem>
                    <MenuItem value={"VOTER_ID"}>Voter Id</MenuItem>
                    <MenuItem value={"PASSPORT"}>Passport</MenuItem>
                    <MenuItem value={"DRIVING_LICENSE"}>Driving License</MenuItem>
                    <MenuItem value={"RATION_CARD"}>Ration Card</MenuItem>
                    <MenuItem value={"ELECTRICITY_BILL"}>Electricity Bill</MenuItem>
                    <MenuItem value={"TELEPHONE_BILL"}>Telephone Bill</MenuItem>
                    <MenuItem value={"GAS_BILL"}>Gas Bill</MenuItem>
                    <MenuItem value={"BANK_PASSBOOK"}>Bank Passbook</MenuItem>
                    <MenuItem value={"RENT_AGREEMENT"}>Rent Agreement</MenuItem>
                    <MenuItem value={"PROPERTY_TAX_RECEIPT"}>Property Tax Receipt</MenuItem>
                    <MenuItem value={"WATER_BILL"}>Water Bill</MenuItem>
                    <MenuItem value={"GOVT_ID"}>Government Id</MenuItem>
                    <MenuItem value={"GST"}>GST</MenuItem>
                  </ReactHookFormSelect>
                </Grid>
                <Grid item xs={12} md={4}>
                  <CustomTextField
                    label="Address Proof Number"
                    name="addressProofNo"
                    control={control}
                    fullWidth
                    variant="outlined"
                    errors={errors}
                    fields={validationFields}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CustomFileField
                    label={t("address_proof")}
                    name="addressProof"
                    control={control}
                    fullWidth
                    variant="outlined"
                    errors={errors}
                    fields={validationFields}
                    ref={fileInputRef}
                  />
                </Grid>

                {/* PROFILE PHOTO */}
                <Grid item xs={12} md={5}>
                  <CustomFileField
                    label={t("profile_proof")}
                    name="profileProof"
                    control={control}
                    fullWidth
                    variant="outlined"
                    errors={errors}
                    fields={validationFields}
                    ref={fileInputRef}
                  />
                </Grid>

                <Grid item xs={12} md={7}>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    JPG / PNG • Max 2MB
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        {Object.keys(cafForm).length != 0 && (
          <Grid item xs={12} md={12}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={4} key={cafForm?.addressProofPath}>
                <KycPreview
                  title={"Address Proof"}
                  fileName={cafForm.addressProofPath.split("/").pop()}
                  previewUrl={images?.addressProof}
                />

                <Stack direction="row" spacing={1} justifyContent="space-between">
                  <Button size="small" onClick={() => downloadKycDocument(null, null)}>
                    Download
                  </Button>

                  {approveOrRejectPermission() && isPending(null) && (
                    <Stack direction="row" spacing={1}>
                      <Button size="small" color="success" onClick={() => approveClick(null)}>
                        Approve
                      </Button>
                      <Button size="small" color="error" onClick={() => rejectClick(null)}>
                        Reject
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} md={4} key={cafForm.idProofPath}>
                <KycPreview
                  title={"Id Proof"}
                  fileName={cafForm.idProofPath.split("/").pop()}
                  previewUrl={images?.idProof}
                />

                <Stack direction="row" spacing={1} justifyContent="space-between">
                  <Button size="small" onClick={() => downloadKycDocument(null, null)}>
                    Download
                  </Button>

                  {approveOrRejectPermission() && isPending(null) && (
                    <Stack direction="row" spacing={1}>
                      <Button size="small" color="success" onClick={() => approveClick(null)}>
                        Approve
                      </Button>
                      <Button size="small" color="error" onClick={() => rejectClick(null)}>
                        Reject
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} md={4} key={cafForm.idProofPath}>
                <KycPreview
                  title="Profile Photo"
                  fileName={cafForm.idProofPath.split("/").pop()}
                  previewUrl={images?.idProof}
                />

                <Stack direction="row" spacing={1} justifyContent="space-between">
                  <Button size="small" onClick={() => downloadKycDocument(null, null)}>
                    Download
                  </Button>

                  {approveOrRejectPermission() && isPending(null) && (
                    <Stack direction="row" spacing={1}>
                      <Button size="small" color="success" onClick={() => approveClick(null)}>
                        Approve
                      </Button>
                      <Button size="small" color="error" onClick={() => rejectClick(null)}>
                        Reject
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Grid>
              {/* <Grid item xs={12} md={4} key={doc.kycDocumentId}>
                <KycPreview doc={doc} previewUrl={images[doc.fileName]} />

                <Stack direction="row" spacing={1} justifyContent="space-between">
                  <Button
                    size="small"
                    onClick={() => downloadKycDocument(images[doc.fileName], doc.fileName)}
                  >
                    Download
                  </Button>

                  {approveOrRejectPermission() && isPending(doc.status) && (
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        color="success"
                        onClick={() => approveClick(doc.kycDocumentId)}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => rejectClick(doc.kycDocumentId)}
                      >
                        Reject
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Grid> */}
            </Grid>
          </Grid>
        )}
      </Grid>
    );
  }
}

export default KycDocuments;
